import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Clock, GraduationCap, MapPin, CheckCircle2, Calendar, Star, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatFee, initials, resolveImg } from "@/lib/format";

export default function CourseDetail() {
  const { courseSlug, instSlug } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/courses/${courseSlug}/${instSlug}`)
      .then((r) => setCourse(r.data))
      .catch(() => setError(true));
  }, [courseSlug, instSlug]);

  if (error) return <NotFound />;
  if (!course) return <div className="mx-auto max-w-5xl px-4 py-20"><div className="h-64 animate-pulse rounded-xl bg-slate-200" /></div>;

  const inst = course.institution || {};
  const visit = async () => {
    try { await api.post(`/public/track/website-click/${inst.id}`); } catch {}
    window.open(inst.website, "_blank", "noopener");
  };

  return (
    <div className="bg-slate-50">
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
        <Link to="/courses" className="mb-4 inline-flex items-center text-sm text-slate-500 hover:text-blue-800">
          <ChevronLeft className="h-4 w-4" /> Back to search
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {inst.logo ? (
                <img src={resolveImg(inst.logo)} alt={inst.name} className="h-16 w-16 rounded-lg object-cover" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-blue-50 font-heading text-xl font-bold text-blue-800">{initials(inst.name)}</span>
              )}
              <div>
                <Link to={`/universities/${inst.slug}`} className="font-heading text-sm font-medium text-blue-800 hover:underline">{inst.name}</Link>
                <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{course.course_name}</h1>
                <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                  <MapPin className="h-4 w-4" /> {[inst.city, inst.country].filter(Boolean).join(", ")}
                </p>
              </div>
            </div>
            {inst.featured && (
              <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                <Star className="h-3.5 w-3.5 fill-current" /> Featured University
              </span>
            )}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-5 sm:grid-cols-4">
            <Info icon={Clock} label="Duration" value={course.duration} />
            <Info icon={GraduationCap} label="Mode" value={course.mode} />
            <Info icon={Calendar} label="Level" value={course.level} />
            <Info label="Total Fee" value={formatFee(course.fee, course.currency)} highlight />
          </div>

          {course.specialisations?.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 font-heading font-semibold text-slate-900">Specialisations</h3>
              <div className="flex flex-wrap gap-2">
                {course.specialisations.map((s) => (
                  <span key={s} className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800">{s}</span>
                ))}
              </div>
            </div>
          )}

          {course.eligibility && (
            <div className="mt-6">
              <h3 className="mb-2 font-heading font-semibold text-slate-900">Eligibility</h3>
              <p className="flex items-start gap-2 text-slate-600"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-800" />{course.eligibility}</p>
            </div>
          )}

          <div className="mt-6">
            <h3 className="mb-2 font-heading font-semibold text-slate-900">About the Programme</h3>
            <p className="whitespace-pre-line text-slate-600">{course.description || "No description provided."}</p>
          </div>

          {inst.description && (
            <div className="mt-6">
              <h3 className="mb-2 font-heading font-semibold text-slate-900">About {inst.name}</h3>
              <p className="text-slate-600">{inst.description}</p>
              {inst.accreditation && <p className="mt-2 text-sm text-slate-500">Accreditation: {inst.accreditation}</p>}
            </div>
          )}

          <div className="mt-8 rounded-xl border border-blue-100 bg-blue-50 p-6 text-center">
            <p className="mb-4 text-sm text-slate-600">
              Get complete, up-to-date information directly from the university.
            </p>
            <Button size="lg" onClick={visit} disabled={!inst.website} className="bg-blue-800 hover:bg-blue-900" data-testid="visit-university-btn">
              VISIT UNIVERSITY WEBSITE <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Course details are provided for informational purposes and may change. Please verify with the institution.
          </p>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, highlight }) {
  return (
    <div>
      <p className="flex items-center gap-1 text-xs text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</p>
      <p className={`mt-0.5 font-heading font-semibold ${highlight ? "text-blue-800" : "text-slate-900"}`}>{value || "—"}</p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Course not found</h1>
      <p className="mt-2 text-slate-500">This programme may no longer be available.</p>
      <Button asChild className="mt-6 bg-blue-800 hover:bg-blue-900"><Link to="/courses">Browse courses</Link></Button>
    </div>
  );
}
